import React, {useEffect} from 'react';
import {useEditor, EditorContent} from "@tiptap/react";
import StarterKit from '@tiptap/starter-kit'
import Bold from '@tiptap/extension-bold'
import Italic from '@tiptap/extension-italic'
import Underline from '@tiptap/extension-underline'
import Link from '@tiptap/extension-link'
import {FaBold, FaItalic, FaUnderline, FaImage, FaFile} from "react-icons/fa";
import {MAX_FILE_SIZE_COMMENT, MAX_ALL_FILE_SIZE_COMMENT, MAX_FILE_COUNT_COMMENT} from "../../../constants/app";
import ReadonlyComment from "../../ReadonlyComment";
import {CustomBackspaceHandler, CustomImage, formatContentEditor, createNewFileName} from "../../../utils/tiptap-utils";
import {createThumbnail} from "../../../utils/common-utils"

const checkDocumentContent = (state) => {
  const {doc} = state;
  let fileCount = 0;
  let totalSize = 0;
  doc.content.forEach(block => {
    block.content.forEach(node => {
      if (node.type.name === 'image') {
        const size = parseFloat(node.attrs['data-size']) || 0;
        fileCount += 1;
        totalSize += size;
      }
      if (node.marks && node.marks.some(mark => mark.type.name === 'link')) {
        const mark = node.marks.find((mark) => mark.type.name === 'link');
        const size = parseFloat(mark.attrs['data-size']) || 0;
        totalSize += size;
        fileCount += 1;
      }
    })
  });
  return {fileCount, totalSize};
};

const MenuBar = ({editor, setCommentFiles, setCommentImagesThumb, canEdit}) => {
  if (!editor) {
    return null;
  }

  const addImage = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileId = createNewFileName(file);
      const {state} = editor.view;
      const fileSizeMB = file.size / 1024 / 1024;

      const result = checkDocumentContent(state);
      if (result.fileCount + 1  >  MAX_FILE_COUNT_COMMENT) {
        alert('ファイルのアップロード可能枚数が5枚を超えています。' );
        return;
      }

      if (fileSizeMB > MAX_FILE_SIZE_COMMENT) {
        alert('ファイルの合計サイズが5MBを超えています。');
        return;
      }

      if (result.totalSize + fileSizeMB > MAX_ALL_FILE_SIZE_COMMENT) {
        alert('ファイルの合計サイズが15MBを超えています。' );
        return;
      }

      setCommentFiles((prev) => [
        ...prev,
        {id: fileId, file: file}
      ]);

      createThumbnail(file, (thumbnailFile) => {
        editor.chain().focus().setImage({
          src: URL.createObjectURL(thumbnailFile),
          'data-id': fileId,
          'data-size': fileSizeMB
        }).run();
        setCommentImagesThumb((prev) => [
          ...prev,
          {id: fileId, file: thumbnailFile}
        ]);
      });
      event.target.value = null;
    }
  };

  const addFile = (event) => {
    const file = event.target.files[0];
    if (file && (file.type === 'application/pdf' || file.type === 'image/gif')) {
      const fileId = createNewFileName(file);
      const reader = new FileReader();
      reader.onload = () => {
        const {state} = editor.view;
        const fileSizeMB = file.size / 1024 / 1024;

        const result = checkDocumentContent(state);
        if (result.fileCount + 1 > MAX_FILE_COUNT_COMMENT) {
          alert('ファイルのアップロード可能枚数が5枚を超えています。');
          return
        }

        if (fileSizeMB > MAX_FILE_SIZE_COMMENT) {
          alert('ファイルの合計サイズが5MBを超えています。');
          return;
        }

        if (result.totalSize + fileSizeMB > MAX_ALL_FILE_SIZE_COMMENT) {
          alert('ファイルの合計サイズが15MBを超えています。');
          return
        }

        const fileName = file.name;
        editor.chain().focus().insertContent([
          {type: 'text', text: '['},
          {
            type: 'text',
            text: fileName,
            marks: [
              {
                type: 'link',
                attrs: {
                  href: "#",
                  class: 'custom-link',
                  'data-id': fileId,
                  'data-size': fileSizeMB
                }
              }
            ]
          },
          {type: 'text', text: ']'},
        ]).run();
      };
      reader.readAsDataURL(file);
      setCommentFiles((prev) => [
        ...prev,
        {id: fileId, file: file}
      ]);
      event.target.value = null;
    }
  };

  return (
    <div className="p-1 bg-[#E0E0E0] items-center">
      <div className="flex border border-[#ccc] bg-white w-fit rounded-md">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={!editor.can().chain().focus().toggleBold().run() || !canEdit}
          className={`flex justify-center items-center w-8 h-8 border border-[#ccc] hover:bg-gray-100 ${editor.isActive('bold') ? 'is-active' : ''}`}
        >
          <FaBold size={14}/>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={!editor.can().chain().focus().toggleItalic().run() || !canEdit}
          className={`flex justify-center items-center w-8 h-8 border border-[#ccc] ${canEdit ? 'hover:bg-gray-100' : ''} ${editor.isActive('italic') ? 'is-active' : ''}`}
        >
          <FaItalic size={14}/>
        </button>
        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={!editor.can().chain().focus().toggleUnderline().run() || !canEdit}
          className={`flex justify-center items-center w-8 h-8 border border-[#ccc] ${canEdit ? 'hover:bg-gray-100' : ''} ${editor.isActive('underline') ? 'is-active' : ''}`}
        >
          <FaUnderline size={14}/>
        </button>
        <input
          type="file"
          accept="image/png, image/jpeg"
          onChange={addImage}
          className="hidden"
          id="file-input-image"
        />
        <input
          type="file"
          accept="application/pdf, image/gif"
          className="hidden"
          onChange={addFile}
          id="file-input"
        />
        <label htmlFor="file-input-image"
               className={`inline-block flex justify-center items-center w-8 h-8 border border-[#ccc] hover:bg-gray-100 cursor-pointer ${!canEdit ? 'pointer-events-none' : ''}`}>
          <FaImage size={14}/>
        </label>
        <label htmlFor="file-input"
               className={`inline-block flex justify-center items-center w-8 h-8 border border-[#ccc] hover:bg-gray-100 cursor-pointer ${!canEdit ? 'pointer-events-none' : ''}`}>
          <FaFile size={14}/>
        </label>
      </div>
    </div>
  )
};

const TextEditor = (props) => {
  const {
    remarkText = null,
    commentFiles=null,
    onChange,
    setCommentFiles,
    setCommentImagesThumb,
    name,
    canEdit = true
  } = props;

  const countCharacters= (jsonContent) => {
    let totalCharacters = 0;
    const countTextNodes = (node) => {
      if (node.type === 'paragraph' && node.content) {
        node.content.forEach(childNode => {
          if (childNode.type === 'text' && !childNode.marks) {
            totalCharacters += childNode.text.length;
          }
        });
      }

      if (node.content) {
        node.content.forEach(countTextNodes);
      }
    };

    countTextNodes(jsonContent);
    return totalCharacters;
  };

  const CustomLink = Link.extend({
    addAttributes() {
      return {
        ...this.parent?.(),
        'data-id': {
          default: null,
        },
        'data-size': {
          default: null
        }
      }
    }
  });

  const editor = useEditor({
    editable: canEdit,
    extensions: [Bold, Italic, Underline, CustomBackspaceHandler, CustomImage,
      StarterKit.configure({
        history: false,
      }),
      CustomLink.configure({
        openOnClick: false,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
        }
      })],
    onUpdate: ({editor}) => {
      const htmlContent = editor.getHTML();
      if (!htmlContent.includes('<img')) {
        setCommentImagesThumb([]);
      }
      if (!htmlContent.includes('<a') && !htmlContent.includes('<img')) {
        setCommentFiles([]);
      }
      onChange(name, formatContentEditor(htmlContent));
    },
    editorProps: {
      handleDrop(view, event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        const hasDragFile = event.dataTransfer?.items[0]?.kind === 'file';
        if (!hasDragFile) {
          return false;
        }

        const imagesAndPdf = Array.from(event.dataTransfer.files).filter((file) =>
          /png|jpg|jpeg|pdf|gif/i.test(file.type)
        );
        if (imagesAndPdf.length === 0) {
          return false;
        }

        const result = checkDocumentContent(view.state);

        for (const file of imagesAndPdf) {
          if (result.fileCount + 1 > MAX_FILE_COUNT_COMMENT) {
            alert('ファイルのアップロード可能枚数が5枚を超えています。' );
            return
          }

          const fileSizeMB = file.size / 1024 / 1024;
          if (fileSizeMB > MAX_FILE_SIZE_COMMENT) {
            alert('ファイルの合計サイズが5MBを超えています。');
            return false;
          }

          if (result.totalSize + fileSizeMB > MAX_ALL_FILE_SIZE_COMMENT) {
            alert('ファイルの合計サイズが15MBを超えています。' );
            return
          }
        }

        imagesAndPdf.forEach(file => {
          const fileId = createNewFileName(file);
          const fileSizeMB = file.size / 1024 / 1024;
          if (/png|jpg|jpeg/i.test(file.type)) {
            createThumbnail(file, (thumbnailFile) => {
              const node = editor.schema.nodes.image.create({
                src: URL.createObjectURL(thumbnailFile),
                'data-id': fileId,
                'data-size': fileSizeMB
              });
              setCommentImagesThumb((prev) => [
                ...prev,
                {id: fileId, file: thumbnailFile}
              ]);
              const transaction = view.state.tr.replaceSelectionWith(node);
              view.dispatch(transaction);
            });
          } else if (/pdf|gif/i.test(file.type)) {
            const fileName = file.name;
            editor.chain().focus().insertContent([
              {type: 'text', text: '['},
              {
                type: 'text',
                text: fileName,
                marks: [
                  {
                    type: 'link',
                    attrs: {
                      href: "#",
                      class: 'custom-link',
                      'data-id': fileId,
                      'data-size': fileSizeMB
                    }
                  }
                ]
              },
              {type: 'text', text: ']'},
            ]).run();
          }
          setCommentFiles((prev) => [
            ...prev,
            {id: fileId, file: file}
          ]);
        });
        return true;
      },
      handleTextInput: (view) => {
        const jsonContent = view.state.doc.toJSON();
        const {state} = editor.view;
        const {doc} = state;

        let maxLength = 200;
        doc.content.forEach(block => {
          block.content.forEach(node => {
            if (node.marks && node.marks.some(mark => mark.type.name === 'link')) {
              maxLength = 202;
            }
          })
        });

        const characterCount = countCharacters(jsonContent);
        if (characterCount >= maxLength && view.state.selection.empty) {
          return true;
        }
      },
      handlePaste: (view, event, slice) => {
        const jsonContent = view.state.doc.toJSON();
        const characterCount = countCharacters(jsonContent);

        const {state} = editor.view;
        const {doc} = state;
        let maxLength = 200;
        doc.content.forEach(block => {
          block.content.forEach(node => {
            if (node.marks && node.marks.some(mark => mark.type.name === 'link')) {
              maxLength = 202;
            }
          })
        });
        return characterCount + slice.size > maxLength
      }
    }
  });

  const updateHTMLWithImage = (htmlString, files) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, 'text/html');
    if (files) {
      files.forEach((file) => {
        if (file?.image_thumbnail) {
          const imgTags = doc.querySelectorAll(`img[data-id="${file.file_name}"]`);
          imgTags.forEach((img) => {
            img.setAttribute('src', file.image_thumbnail);
          })
        }
      });
    }

    return doc.body.innerHTML;
  };

  useEffect(() => {
    if (editor && remarkText) {
      const htmlWithImage = updateHTMLWithImage(remarkText, commentFiles);
      editor.commands.setContent(htmlWithImage)
    }
  }, [editor, remarkText]);

  return (
    canEdit ? (
      <div className="flex flex-col w-full 2xl:max-w-[1587px] xl:max-w-[1219px] max-w-[1116px] rounded-lg border border-[#D0D5DD] bg-white">
        <MenuBar editor={editor} canEdit={canEdit} setCommentFiles={setCommentFiles} setCommentImagesThumb={setCommentImagesThumb}/>
        <EditorContent
          editor={editor}
          className="px-[12px] py-[10px] tenken__text-gray-900"
          onDragOver={(event) => event.preventDefault()}
          onDrop={(event) => {
            editor.view.someProp('handleDrop', f => f(editor.view, event))
          }}
        />
      </div>
    ) : (
      <ReadonlyComment
        remarkText={remarkText}
        commentFiles={commentFiles}
      />
    )
  )
};

export default TextEditor;